import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  FileText,
  Link2,
  BarChart3,
  DollarSign,
  Paperclip,
  Loader2,
  AlertTriangle,
  Search,
  X,
  Check,
  Info
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { createClaim, getAvailableOpportunities, uploadDocument } from '../api/claims';
import FileUpload from '../components/claims/FileUpload';

// Claim type configurations
const CLAIM_TYPES = [
  {
    id: 'volume_shortage',
    label: 'Volume Shortage',
    description: 'Customer ordered less volume than contracted'
  },
  {
    id: 'price_deviation',
    label: 'Price Deviation',
    description: 'Price paid differs from contracted price'
  },
  {
    id: 'material_cost',
    label: 'Material Cost',
    description: 'Unexpected material cost increases'
  },
  {
    id: 'exchange_rate',
    label: 'Exchange Rate',
    description: 'Currency fluctuation impacts'
  },
  {
    id: 'other',
    label: 'Other',
    description: 'Other types of claims'
  }
];

const ClaimCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Section expand/collapse states
  const [claimTypeOpen, setClaimTypeOpen] = useState(true);
  const [opportunityOpen, setOpportunityOpen] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [financialOpen, setFinancialOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    claim_type: 'volume_shortage',
    period_type: 'quarterly',
    time_period: '',
    oem: '',
    part_number: '',
    contracted_volume: '',
    actual_volume: '',
    contracted_price_dollars: '',
    actual_price_dollars: '',
    financial_impact_dollars: '',
    notes: '',
    opportunity_id: null
  });

  const [files, setFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [opportunitySearch, setOpportunitySearch] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Track form changes
  useEffect(() => {
    const hasData =
      formData.time_period ||
      formData.oem ||
      formData.part_number ||
      formData.notes ||
      files.length > 0;
    setHasUnsavedChanges(hasData);
  }, [formData, files]);

  // Search opportunities
  const { data: opportunitiesData, isLoading: opportunitiesLoading } = useQuery({
    queryKey: ['availableOpportunities', opportunitySearch],
    queryFn: () => getAvailableOpportunities(opportunitySearch),
    enabled: opportunityOpen && opportunitySearch.length > 0
  });

  const opportunities = opportunitiesData?.data || [];

  // Create claim mutation
  const createMutation = useMutation({
    mutationFn: createClaim,
    onSuccess: async (response) => {
      const claimId = response.data.id;

      // Upload files if any
      if (files.length > 0) {
        setIsUploadingFiles(true);
        setUploadProgress({ current: 0, total: files.length });

        try {
          for (let i = 0; i < files.length; i++) {
            setUploadProgress({ current: i + 1, total: files.length });
            await uploadDocument(claimId, files[i], 'claim_definition', 'evidence');
          }
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          // Continue to claim page even if uploads fail - user can retry there
        } finally {
          setIsUploadingFiles(false);
        }
      }

      queryClient.invalidateQueries(['claims']);
      navigate(`/claims/${claimId}`);
    },
    onError: (error) => {
      setValidationErrors([error.message || 'Failed to create claim']);
    }
  });

  // Auto-fill from opportunity
  useEffect(() => {
    if (selectedOpportunity) {
      setFormData(prev => ({
        ...prev,
        oem: selectedOpportunity.oem || prev.oem,
        part_number: selectedOpportunity.part_number || prev.part_number,
        opportunity_id: selectedOpportunity.id
      }));

      // If opportunity has volume data, pre-fill
      if (selectedOpportunity.contracted_volume) {
        setFormData(prev => ({
          ...prev,
          contracted_volume: selectedOpportunity.contracted_volume
        }));
      }
    }
  }, [selectedOpportunity]);

  // Auto-calculate financial impact
  const calculateFinancialImpact = () => {
    const shortage = parseFloat(formData.contracted_volume || 0) - parseFloat(formData.actual_volume || 0);
    const contractedPrice = parseFloat(formData.contracted_price_dollars || 0);

    if (shortage > 0 && contractedPrice > 0) {
      const impact = shortage * contractedPrice;
      setFormData(prev => ({
        ...prev,
        financial_impact_dollars: impact.toFixed(2)
      }));
    }
  };

  // Calculate shortage
  const getShortageInfo = () => {
    const contracted = parseFloat(formData.contracted_volume || 0);
    const actual = parseFloat(formData.actual_volume || 0);
    const shortage = contracted - actual;
    const percentage = contracted > 0 ? ((shortage / contracted) * 100).toFixed(1) : 0;

    return { shortage, percentage };
  };

  // Link opportunity
  const handleSelectOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setOpportunitySearch('');
  };

  // Clear opportunity link
  const handleClearOpportunity = () => {
    setSelectedOpportunity(null);
    setFormData(prev => ({
      ...prev,
      opportunity_id: null
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = [];

    if (!formData.time_period) {
      errors.push('Time period is required');
    }

    if (!formData.financial_impact_dollars || parseFloat(formData.financial_impact_dollars) <= 0) {
      errors.push('Financial impact is required and must be greater than 0');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Convert dollars to cents
    const dataToSubmit = {
      claim_type: formData.claim_type,
      period_type: formData.period_type,
      time_period: formData.time_period,
      oem: formData.oem || null,
      part_number: formData.part_number || null,
      opportunity_id: formData.opportunity_id,
      contracted_volume: formData.contracted_volume ? parseInt(formData.contracted_volume) : null,
      actual_volume: formData.actual_volume ? parseInt(formData.actual_volume) : null,
      contracted_price_cents: formData.contracted_price_dollars
        ? Math.round(parseFloat(formData.contracted_price_dollars) * 100)
        : null,
      actual_price_cents: formData.actual_price_dollars
        ? Math.round(parseFloat(formData.actual_price_dollars) * 100)
        : null,
      financial_impact_cents: Math.round(parseFloat(formData.financial_impact_dollars) * 100),
      notes: formData.notes || null
    };

    createMutation.mutate(dataToSubmit);
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate('/claims');
      }
    } else {
      navigate('/claims');
    }
  };

  // Get section status badge
  const getSectionBadge = (section) => {
    switch (section) {
      case 'claim_type':
        return formData.claim_type && formData.time_period ? (
          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
            <Check className="w-3 h-3 mr-1" />
            Complete
          </Badge>
        ) : null;
      case 'opportunity':
        return selectedOpportunity ? (
          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
            <Check className="w-3 h-3 mr-1" />
            Linked
          </Badge>
        ) : null;
      case 'volume':
        return formData.contracted_volume && formData.actual_volume ? (
          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
            <Check className="w-3 h-3 mr-1" />
            Complete
          </Badge>
        ) : null;
      case 'financial':
        return formData.financial_impact_dollars ? (
          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
            <Check className="w-3 h-3 mr-1" />
            Complete
          </Badge>
        ) : null;
      case 'evidence':
        return files.length > 0 || formData.notes ? (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
            {files.length > 0 && `${files.length} files`}
            {files.length > 0 && formData.notes && ' + '}
            {formData.notes && 'notes'}
          </Badge>
        ) : null;
      default:
        return null;
    }
  };

  const { shortage, percentage } = getShortageInfo();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Claims
            </Button>
          </div>
          <div className="mt-4">
            <h1 className="text-2xl font-semibold text-gray-900">Create New Claim</h1>
            <p className="text-sm text-gray-600 mt-1">
              Document a financial claim against a customer or supplier
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-semibold mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* 1. CLAIM TYPE SECTION */}
          <Collapsible open={claimTypeOpen} onOpenChange={setClaimTypeOpen}>
            <Card className="border border-gray-200">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {claimTypeOpen ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      <FileText className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-lg">Claim Type</CardTitle>
                    </div>
                    {getSectionBadge('claim_type')}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-6">
                  {/* Claim Type Radio Group */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Select Claim Type</Label>
                    <RadioGroup
                      value={formData.claim_type}
                      onValueChange={(value) => {
                        setFormData({ ...formData, claim_type: value });
                        // Auto-expand volume section if volume_shortage
                        if (value === 'volume_shortage') {
                          setVolumeOpen(true);
                        }
                      }}
                    >
                      {CLAIM_TYPES.map((type) => (
                        <div
                          key={type.id}
                          className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <RadioGroupItem value={type.id} id={type.id} className="mt-1" />
                          <div className="flex-1">
                            <Label htmlFor={type.id} className="font-medium cursor-pointer">
                              {type.label}
                            </Label>
                            <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  {/* Period Type Toggle */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Period Type</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={formData.period_type === 'quarterly' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, period_type: 'quarterly', time_period: '' })}
                        className="flex-1"
                      >
                        Quarterly
                      </Button>
                      <Button
                        type="button"
                        variant={formData.period_type === 'monthly' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, period_type: 'monthly', time_period: '' })}
                        className="flex-1"
                      >
                        Monthly
                      </Button>
                    </div>
                  </div>

                  {/* Time Period Input */}
                  <div className="space-y-2">
                    <Label htmlFor="time_period" className="text-sm font-medium">
                      Time Period <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="time_period"
                      type="text"
                      value={formData.time_period}
                      onChange={(e) => setFormData({ ...formData, time_period: e.target.value })}
                      placeholder={formData.period_type === 'quarterly' ? '2025-Q1' : '2025-01'}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500">
                      {formData.period_type === 'quarterly'
                        ? 'Format: YYYY-Q# (e.g., 2025-Q1)'
                        : 'Format: YYYY-MM (e.g., 2025-01)'}
                    </p>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* 2. OPPORTUNITY LINK SECTION */}
          <Collapsible open={opportunityOpen} onOpenChange={setOpportunityOpen}>
            <Card className="border border-gray-200">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {opportunityOpen ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      <Link2 className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-lg">Opportunity Link (Optional)</CardTitle>
                    </div>
                    {getSectionBadge('opportunity')}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  {selectedOpportunity ? (
                    // Show selected opportunity
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Check className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-gray-900">
                              Linked to Opportunity
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 font-medium">
                            {selectedOpportunity.opportunity_name}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                            <span>{selectedOpportunity.customer_name}</span>
                            {selectedOpportunity.part_number && (
                              <>
                                <span>•</span>
                                <span>Part: {selectedOpportunity.part_number}</span>
                              </>
                            )}
                            {selectedOpportunity.oem && (
                              <>
                                <span>•</span>
                                <span>OEM: {selectedOpportunity.oem}</span>
                              </>
                            )}
                          </div>
                          {selectedOpportunity.volume_variance_percentage && (
                            <Badge variant="warning" className="mt-2">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {Math.abs(selectedOpportunity.volume_variance_percentage)}% below forecast
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearOpportunity}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Search for opportunities
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="opportunity-search">Search Opportunities</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="opportunity-search"
                            type="text"
                            value={opportunitySearch}
                            onChange={(e) => setOpportunitySearch(e.target.value)}
                            placeholder="Search by opportunity name, customer, part number..."
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Search Results */}
                      {opportunitiesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                          <span className="ml-2 text-sm text-gray-600">Searching...</span>
                        </div>
                      ) : opportunities.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {opportunities.map((opportunity) => (
                            <div
                              key={opportunity.id}
                              onClick={() => handleSelectOpportunity(opportunity)}
                              className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 text-sm">
                                    {opportunity.opportunity_name}
                                  </h4>
                                  <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                                    <span>{opportunity.customer_name}</span>
                                    {opportunity.part_number && (
                                      <>
                                        <span>•</span>
                                        <span>Part: {opportunity.part_number}</span>
                                      </>
                                    )}
                                    {opportunity.oem && (
                                      <>
                                        <span>•</span>
                                        <span>OEM: {opportunity.oem}</span>
                                      </>
                                    )}
                                  </div>
                                  {opportunity.volume_variance_percentage && (
                                    <Badge variant="warning" className="mt-2 text-xs">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      {Math.abs(opportunity.volume_variance_percentage)}% below forecast
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : opportunitySearch.length > 0 ? (
                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            No opportunities found. Try a different search term.
                          </AlertDescription>
                        </Alert>
                      ) : null}
                    </>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* 3. VOLUME DETAILS SECTION (conditional) */}
          {formData.claim_type === 'volume_shortage' && (
            <Collapsible open={volumeOpen} onOpenChange={setVolumeOpen}>
              <Card className="border border-gray-200">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {volumeOpen ? (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        )}
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        <CardTitle className="text-lg">Volume Details</CardTitle>
                      </div>
                      {getSectionBadge('volume')}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contracted_volume">Contracted Volume</Label>
                        <Input
                          id="contracted_volume"
                          type="number"
                          value={formData.contracted_volume}
                          onChange={(e) =>
                            setFormData({ ...formData, contracted_volume: e.target.value })
                          }
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="actual_volume">Actual Volume</Label>
                        <Input
                          id="actual_volume"
                          type="number"
                          value={formData.actual_volume}
                          onChange={(e) =>
                            setFormData({ ...formData, actual_volume: e.target.value })
                          }
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Calculated Display */}
                    {formData.contracted_volume && formData.actual_volume && (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Shortage</p>
                            <p className={`text-lg font-semibold ${
                              shortage > 0 ? 'text-red-600' : 'text-gray-900'
                            }`}>
                              {shortage.toLocaleString()} units
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Percentage</p>
                            <p className={`text-lg font-semibold ${
                              shortage > 0 ? 'text-red-600' : 'text-gray-900'
                            }`}>
                              {percentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {/* 4. FINANCIAL IMPACT SECTION */}
          <Collapsible open={financialOpen} onOpenChange={setFinancialOpen}>
            <Card className="border border-gray-200">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {financialOpen ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      <DollarSign className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-lg">Financial Impact</CardTitle>
                    </div>
                    {getSectionBadge('financial')}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contracted_price">Contracted Price ($)</Label>
                      <Input
                        id="contracted_price"
                        type="number"
                        step="0.01"
                        value={formData.contracted_price_dollars}
                        onChange={(e) =>
                          setFormData({ ...formData, contracted_price_dollars: e.target.value })
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="actual_price">Actual Price ($)</Label>
                      <Input
                        id="actual_price"
                        type="number"
                        step="0.01"
                        value={formData.actual_price_dollars}
                        onChange={(e) =>
                          setFormData({ ...formData, actual_price_dollars: e.target.value })
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="financial_impact">
                        Financial Impact ($) <span className="text-red-600">*</span>
                      </Label>
                      {formData.claim_type === 'volume_shortage' &&
                        formData.contracted_volume &&
                        formData.actual_volume &&
                        formData.contracted_price_dollars && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={calculateFinancialImpact}
                          >
                            Auto-Calculate
                          </Button>
                        )}
                    </div>
                    <Input
                      id="financial_impact"
                      type="number"
                      step="0.01"
                      value={formData.financial_impact_dollars}
                      onChange={(e) =>
                        setFormData({ ...formData, financial_impact_dollars: e.target.value })
                      }
                      placeholder="0.00"
                      className="text-lg font-semibold"
                    />
                    {formData.claim_type === 'volume_shortage' && (
                      <p className="text-xs text-gray-500">
                        Auto-calculate formula: (Contracted Volume - Actual Volume) × Contracted Price
                      </p>
                    )}
                  </div>

                  {formData.financial_impact_dollars && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-gray-700 mb-1">Total Financial Impact</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ${parseFloat(formData.financial_impact_dollars).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* 5. EVIDENCE & NOTES SECTION */}
          <Collapsible open={evidenceOpen} onOpenChange={setEvidenceOpen}>
            <Card className="border border-gray-200">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {evidenceOpen ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      <Paperclip className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-lg">Evidence & Notes</CardTitle>
                    </div>
                    {getSectionBadge('evidence')}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label>Supporting Documents</Label>
                    <FileUpload files={files} onChange={setFiles} />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={6}
                      placeholder="Add detailed notes about this claim, including relevant context, communications, or supporting information..."
                      className="resize-none"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || isUploadingFiles}
              className="gap-2"
            >
              {createMutation.isPending && !isUploadingFiles ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Claim...
                </>
              ) : isUploadingFiles ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading Files ({uploadProgress.current}/{uploadProgress.total})...
                </>
              ) : (
                <>Create Claim</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom padding to prevent content from being hidden by sticky footer */}
      <div className="h-20" />
    </div>
  );
};

export default ClaimCreate;
